import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Download, Database, FileArchive, FileSpreadsheet, RefreshCw,
  ShieldAlert, Trash2, Clock, History, AlertTriangle, Lock,
  Server, Play, FileCode2, Copy, CheckCircle2, XCircle,
  RotateCcw, Search,
} from "lucide-react";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  if (n > 1024 * 1024) return (n / 1024 / 1024).toFixed(2) + " MB";
  if (n > 1024) return (n / 1024).toFixed(1) + " KB";
  return n + " B";
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function BackupsPage() {
  const { axiosAuth, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingFull, setGeneratingFull] = useState(false);
  const [generatingModule, setGeneratingModule] = useState(null);
  const [sysStatus, setSysStatus] = useState(null);
  const [sysItems, setSysItems] = useState({ beta: [], produccion: [] });
  const [sysLoading, setSysLoading] = useState(false);
  const [runningBeta, setRunningBeta] = useState(false);
  const [lastRunLog, setLastRunLog] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreReport, setRestoreReport] = useState(null);

  const isSuperAdmin = user?.role === "super_admin";

  const fetchSummary = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const res = await axiosAuth().get("/admin/backups/summary");
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando resumen");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, isSuperAdmin]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchSystem = useCallback(async () => {
    if (!isSuperAdmin) return;
    setSysLoading(true);
    try {
      const [statusRes, betaRes, prodRes] = await Promise.all([
        axiosAuth().get("/admin/backups/system/status"),
        axiosAuth().get("/admin/backups/system/list/beta"),
        axiosAuth().get("/admin/backups/system/list/produccion"),
      ]);
      setSysStatus(statusRes.data);
      setSysItems({ beta: betaRes.data.items || [], produccion: prodRes.data.items || [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando backups del sistema");
    } finally {
      setSysLoading(false);
    }
  }, [axiosAuth, isSuperAdmin]);

  useEffect(() => { fetchSystem(); }, [fetchSystem]);

  const downloadSystem = async (env, filename) => {
    try {
      const res = await axiosAuth().get(
        `/admin/backups/system/download/${env}/${encodeURIComponent(filename)}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error descargando tarball del sistema");
    }
  };

  const runBackupBeta = async () => {
    if (!window.confirm(
      "Vas a ejecutar el script backup-beta en el servidor.\n\n" +
      "Esto generará un tarball completo (BD + uploads + configs + nginx) en /opt/backups/ascauto/beta.\n\n" +
      "¿Confirmar paso 1 de 2?"
    )) return;
    if (!window.confirm("Confirmación final: ¿ejecutar AHORA backup-beta?")) return;
    setRunningBeta(true);
    setLastRunLog(null);
    try {
      const res = await axiosAuth().post(
        "/admin/backups/system/run/beta?confirm=CONFIRMAR",
        null,
        { timeout: 660000 },
      );
      setLastRunLog(res.data);
      if (res.data.ok) {
        toast.success(`Backup beta generado en ${res.data.duration_s.toFixed(1)}s`);
      } else {
        toast.error(`Script terminó con código ${res.data.returncode}`);
      }
      fetchSystem();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error ejecutando backup-beta";
      toast.error(msg);
      setLastRunLog({ ok: false, log: msg, returncode: -1 });
    } finally {
      setRunningBeta(false);
    }
  };

  const copyCli = (cmd) => {
    navigator.clipboard?.writeText(cmd);
    toast.success("Comando copiado");
  };

  const verifyBackup = async (env, filename) => {
    setVerifying(`${env}/${filename}`);
    setVerifyResult(null);
    try {
      const res = await axiosAuth().get(
        `/admin/backups/system/verify/${env}/${encodeURIComponent(filename)}`,
        { timeout: 60000 },
      );
      setVerifyResult({ env, filename, ...res.data });
      if (res.data.ok) toast.success("Verificación OK");
      else toast.error(`Verificación con errores (${res.data.errors?.length || 0})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error verificando backup");
    } finally {
      setVerifying(null);
    }
  };

  const restoreBackup = async (env, filename, sections, dryRun) => {
    const expectedConfirm = env === "beta" ? "RESTAURAR-BETA" : "RESTAURAR ascauto.com";
    const userInput = window.prompt(
      `Vas a restaurar el backup:\n${filename}\n\n` +
      `Entorno: ${env.toUpperCase()}\n` +
      `Secciones: ${sections}\n` +
      `Modo: ${dryRun ? "DRY-RUN (no aplica)" : "REAL (APLICA)"}\n\n` +
      `Escribe exactamente: ${expectedConfirm}`
    );
    if (userInput !== expectedConfirm) {
      if (userInput !== null) toast.error("Confirmación incorrecta");
      return;
    }
    if (!dryRun && !window.confirm(
      "Confirmación final: ¿APLICAR la restauración?\n\n" +
      "Se creará un backup previo automático del estado actual antes de tocar nada."
    )) return;

    setRestoring(true);
    setRestoreReport(null);
    try {
      const params = new URLSearchParams({
        filename,
        sections,
        confirm: expectedConfirm,
        dry_run: dryRun ? "true" : "false",
      });
      const res = await axiosAuth().post(
        `/admin/backups/system/restore/${env}?${params.toString()}`,
        null,
        { timeout: 1300000 },
      );
      setRestoreReport(res.data);
      if (res.data.ok) toast.success(`Restauración ${dryRun ? "dry-run " : ""}completada`);
      else toast.error(`Script terminó con rc=${res.data.returncode}`);
      fetchSystem();
    } catch (err) {
      const detail = err.response?.data?.detail || "Error en la restauración";
      toast.error(detail);
      setRestoreReport({ ok: false, log: detail, returncode: -1 });
    } finally {
      setRestoring(false);
    }
  };

  const downloadById = async (backupId, filename) => {
    try {
      const res = await axiosAuth().get(`/admin/backups/download/${backupId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en la descarga");
    }
  };

  const generateFullBackup = async () => {
    if (!window.confirm(
      "ATENCIÓN: vas a generar un backup completo de la base de datos.\n\n" +
      "Incluirá toda la información administrativa (excepto contraseñas y claves API).\n\n" +
      "¿Confirmar paso 1 de 2?"
    )) return;
    if (!window.confirm(
      "Confirmación final (paso 2 de 2):\n\n" +
      "¿Generar AHORA el backup completo? La acción quedará registrada en el log de auditoría."
    )) return;
    setGeneratingFull(true);
    try {
      const res = await axiosAuth().post("/admin/backups/full?confirm=CONFIRMAR", null, { timeout: 180000 });
      toast.success(`Backup creado: ${res.data.total_docs} docs, ${fmtBytes(res.data.size)}`);
      await downloadById(res.data.backup_id, res.data.filename);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error generando backup completo");
    } finally {
      setGeneratingFull(false);
    }
  };

  const exportModule = async (moduleKey, format) => {
    setGeneratingModule(`${moduleKey}-${format}`);
    try {
      const res = await axiosAuth().post(`/admin/backups/export/${moduleKey}?format=${format}`, null, { timeout: 120000 });
      toast.success(`${res.data.total_docs} registros exportados (${format.toUpperCase()})`);
      await downloadById(res.data.backup_id, res.data.filename);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Error exportando ${moduleKey}`);
    } finally {
      setGeneratingModule(null);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm("¿Eliminar este backup del servidor?")) return;
    try {
      await axiosAuth().delete(`/admin/backups/${backupId}`);
      toast.success("Backup eliminado");
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando backup");
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4" data-testid="backups-restricted">
        <Card className="border-amber-200 bg-amber-50/50 rounded-sm">
          <CardContent className="pt-6 flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber-700 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-1">Acceso restringido</h3>
              <p className="text-xs text-amber-800">Solo el rol <strong>super_admin</strong> puede acceder a copias de seguridad.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="backups-page">
      <div>
        <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">
          Copias de seguridad
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Exporta la base de datos completa o por módulos. Los backups generados se guardan {data?.ttl_hours || 24} h en el servidor.
        </p>
      </div>

      {/* Aviso seguridad */}
      <Card className="border-amber-200 bg-amber-50/50 rounded-sm" data-testid="backups-security-notice">
        <CardContent className="pt-4 pb-4 flex items-start gap-3">
          <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong className="block mb-1">Datos sensibles excluidos automáticamente:</strong>
            contraseñas, hashes, tokens, claves API y credenciales SMTP. Cada generación y descarga queda registrada en el log de auditoría.
          </div>
        </CardContent>
      </Card>

      {/* Backup completo */}
      <Card className="border-slate-200 rounded-sm" data-testid="backups-full-card">
        <CardHeader className="pb-3">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <FileArchive className="w-4 h-4 text-red-600" /> Backup completo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Total documentos" value={data?.total_docs ?? "—"} />
            <Stat label="Módulos" value={Object.keys(data?.modules || {}).length} />
            <Stat label="Base de datos" value={data?.db_name || "—"} mono />
            <Stat label="TTL backups" value={`${data?.ttl_hours || 24}h`} />
          </div>
          <Button
            onClick={generateFullBackup}
            disabled={generatingFull}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-10 px-6 text-xs uppercase tracking-wider font-bold"
            data-testid="generate-full-backup-btn"
          >
            <FileArchive className={`w-4 h-4 mr-2 ${generatingFull ? "animate-pulse" : ""}`} />
            {generatingFull ? "Generando..." : "Descargar backup completo (.tar.gz)"}
          </Button>
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Requiere doble confirmación. Tamaño estimado: depende del volumen de datos.
          </p>
        </CardContent>
      </Card>

      {/* Exportaciones por módulo */}
      <Card className="border-slate-200 rounded-sm" data-testid="backups-modules-card">
        <CardHeader className="pb-3">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <Database className="w-4 h-4 text-red-600" /> Exportaciones por módulo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data && Object.entries(data.modules).map(([key, mod]) => (
              <div
                key={key}
                className="border border-slate-200 rounded-sm p-3 flex flex-col gap-2"
                data-testid={`module-card-${key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{mod.label}</p>
                    <p className="text-[10px] text-slate-500 truncate">{mod.collections.join(", ")}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono flex-shrink-0">{mod.docs}</Badge>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    onClick={() => exportModule(key, "json")}
                    disabled={generatingModule !== null}
                    variant="outline"
                    size="sm"
                    className="rounded-sm text-[10px] h-7 px-2.5 uppercase tracking-wider flex-1"
                    data-testid={`export-${key}-json`}
                  >
                    <FileArchive className="w-3 h-3 mr-1" />
                    {generatingModule === `${key}-json` ? "..." : "JSON"}
                  </Button>
                  <Button
                    onClick={() => exportModule(key, "xlsx")}
                    disabled={generatingModule !== null}
                    variant="outline"
                    size="sm"
                    className="rounded-sm text-[10px] h-7 px-2.5 uppercase tracking-wider flex-1"
                    data-testid={`export-${key}-xlsx`}
                  >
                    <FileSpreadsheet className="w-3 h-3 mr-1" />
                    {generatingModule === `${key}-xlsx` ? "..." : "XLSX"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Historial */}
      <Card className="border-slate-200 rounded-sm" data-testid="backups-history-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <History className="w-4 h-4 text-red-600" /> Historial reciente
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchSummary} disabled={loading}
            className="h-7 px-2 text-[10px] uppercase tracking-wider" data-testid="refresh-history">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </CardHeader>
        <CardContent>
          {!data?.history?.length ? (
            <p className="text-xs text-slate-400">Aún no hay backups generados.</p>
          ) : (
            <div className="space-y-1.5">
              {data.history.map(b => (
                <div
                  key={b.id}
                  className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm bg-slate-50/40 text-xs"
                  data-testid={`history-row-${b.id}`}
                >
                  <Badge variant={b.kind === "full" ? "default" : "outline"} className="text-[9px] uppercase">
                    {b.kind === "full" ? "Completo" : b.module}
                  </Badge>
                  <code className="text-[10px] text-slate-700 font-mono truncate flex-1">{b.filename}</code>
                  <span className="text-[10px] text-slate-500 hidden sm:inline">{fmtBytes(b.size)}</span>
                  <span className="text-[10px] text-slate-500 hidden md:inline flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fmtDate(b.created_at)}
                  </span>
                  <span className="text-[10px] text-slate-500 hidden lg:inline">{b.created_by_name}</span>
                  <Button
                    onClick={() => downloadById(b.id, b.filename)}
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0 hover:bg-slate-200"
                    data-testid={`download-${b.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    onClick={() => deleteBackup(b.id)}
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                    data-testid={`delete-${b.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {/* Backups del sistema (host) */}
      <Card className="border-slate-200 rounded-sm" data-testid="system-backups-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="heading-industrial text-base uppercase flex items-center gap-2">
            <Server className="w-4 h-4 text-red-600" /> Backups del sistema (host)
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchSystem} disabled={sysLoading}
            className="h-7 px-2 text-[10px] uppercase tracking-wider"
            data-testid="refresh-system-backups">
            <RefreshCw className={`w-3 h-3 mr-1 ${sysLoading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-slate-600 leading-relaxed">
            Tarballs completos (mongodump + uploads + docker-compose + .env + nginx)
            generados por los scripts <code className="bg-slate-100 px-1 rounded text-[10px]">backup-beta</code>{" "}
            y <code className="bg-slate-100 px-1 rounded text-[10px]">backup-produccion</code> en el VPS.
            Se guardan en <code className="bg-slate-100 px-1 rounded text-[10px]">/opt/backups/ascauto/</code>.
          </p>

          {/* Estado del entorno */}
          {sysStatus && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="system-status-grid">
              {["beta", "produccion"].map(env => {
                const info = sysStatus.envs?.[env] || {};
                const available = info.dir_exists;
                return (
                  <div
                    key={env}
                    className={`border rounded-sm p-3 ${available ? "border-slate-200 bg-slate-50/40" : "border-amber-200 bg-amber-50/40"}`}
                    data-testid={`system-env-${env}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-800">{env}</span>
                      {available ? (
                        <Badge variant="outline" className="text-[9px] border-green-300 text-green-800 bg-green-50">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Disponible
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-800 bg-amber-50">
                          <XCircle className="w-3 h-3 mr-1" /> No disponible aquí
                        </Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 font-mono break-all">{info.dir}</p>
                    <p className="text-[10px] text-slate-500 mt-1">
                      {available ? `${info.count} tarballs encontrados` : "Directorio no montado en este entorno"}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Ejecutar backup-beta */}
          <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50/30" data-testid="system-run-beta">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <FileArchive className="w-4 h-4 text-red-600" /> Crear backup completo de beta
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Ejecuta el script <code className="bg-white border px-1 rounded">backup-beta</code> y
                  genera un nuevo tarball en el host.
                </p>
              </div>
              {sysStatus?.exec_enabled ? (
                <Button
                  onClick={runBackupBeta}
                  disabled={runningBeta || !sysStatus?.envs?.beta?.script_exists}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 px-4 text-xs uppercase tracking-wider font-bold"
                  data-testid="run-backup-beta-btn"
                >
                  <Play className={`w-3.5 h-3.5 mr-2 ${runningBeta ? "animate-pulse" : ""}`} />
                  {runningBeta ? "Ejecutando..." : "Ejecutar backup-beta"}
                </Button>
              ) : (
                <Button
                  onClick={() => copyCli(sysStatus?.cli_command_beta || "sudo bash /opt/ascauto-staging/scripts/backup-beta")}
                  variant="outline"
                  className="rounded-sm h-9 px-4 text-xs uppercase tracking-wider"
                  data-testid="copy-cli-beta"
                >
                  <Copy className="w-3.5 h-3.5 mr-2" /> Copiar comando CLI
                </Button>
              )}
            </div>
            {!sysStatus?.exec_enabled && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2 leading-relaxed" data-testid="exec-disabled-notice">
                <strong>Ejecución desde web deshabilitada en este entorno.</strong> Lánzalo por SSH:
                <code className="block mt-1 font-mono text-[10px] bg-white border rounded px-2 py-1 break-all">
                  {sysStatus?.cli_command_beta || "sudo bash /opt/ascauto-staging/scripts/backup-beta"}
                </code>
              </p>
            )}

            {/* Log última ejecución */}
            {lastRunLog && (
              <div className="mt-2 border rounded-sm p-2 bg-slate-900 text-slate-100 max-h-64 overflow-auto"
                   data-testid="run-log-output">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-1.5 text-slate-300">
                  <FileCode2 className="w-3 h-3" /> Log última ejecución
                  <Badge variant="outline" className={`text-[9px] ${lastRunLog.ok ? "border-green-500 text-green-300" : "border-red-500 text-red-300"}`}>
                    rc={lastRunLog.returncode}
                  </Badge>
                </div>
                <pre className="text-[10px] font-mono whitespace-pre-wrap break-all leading-snug">
                  {lastRunLog.log}
                </pre>
              </div>
            )}
          </div>

          {/* Listado tarballs por entorno */}
          {["beta", "produccion"].map(env => (
            <div key={env} data-testid={`system-list-${env}`}>
              <div className="flex items-center gap-2 mb-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">{env}</h4>
                <Badge variant="outline" className="text-[9px]">{sysItems[env]?.length || 0} tarballs</Badge>
              </div>
              {(!sysItems[env] || sysItems[env].length === 0) ? (
                <p className="text-[11px] text-slate-400 ml-2">Sin tarballs en este entorno.</p>
              ) : (
                <div className="space-y-1">
                  {sysItems[env].map(item => (
                    <div
                      key={item.filename}
                      className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-sm bg-slate-50/40 text-xs flex-wrap"
                      data-testid={`system-row-${env}-${item.filename}`}
                    >
                      <FileArchive className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                      <code className="text-[10px] text-slate-700 font-mono truncate flex-1 min-w-[180px]">{item.filename}</code>
                      <span className="text-[10px] text-slate-500 hidden sm:inline">{fmtBytes(item.size)}</span>
                      <span className="text-[10px] text-slate-500 hidden md:inline flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtDate(item.created_at)}
                      </span>
                      <Button
                        onClick={() => downloadSystem(env, item.filename)}
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 hover:bg-slate-200"
                        data-testid={`system-download-${env}-${item.filename}`}
                        title="Descargar tarball"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        onClick={() => verifyBackup(env, item.filename)}
                        disabled={verifying === `${env}/${item.filename}`}
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 hover:bg-slate-200"
                        data-testid={`system-verify-${env}-${item.filename}`}
                        title="Verificar integridad"
                      >
                        <Search className={`w-3.5 h-3.5 ${verifying === `${env}/${item.filename}` ? "animate-pulse" : ""}`} />
                      </Button>
                      <Button
                        onClick={() => restoreBackup(env, item.filename, "db,uploads", true)}
                        disabled={restoring}
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-[9px] uppercase tracking-wider hover:bg-amber-50 text-amber-700"
                        data-testid={`system-restore-dry-${env}-${item.filename}`}
                        title="Restaurar en dry-run"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Dry
                      </Button>
                      <Button
                        onClick={() => restoreBackup(env, item.filename, "db,uploads", false)}
                        disabled={restoring}
                        size="sm" variant="ghost"
                        className="h-7 px-2 text-[9px] uppercase tracking-wider hover:bg-red-50 text-red-700"
                        data-testid={`system-restore-real-${env}-${item.filename}`}
                        title={`Restaurar (REAL) — requiere escribir "${env === "beta" ? "RESTAURAR-BETA" : "RESTAURAR ascauto.com"}"`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Verify result */}
          {verifyResult && (
            <div className="border border-slate-200 rounded-sm p-3 bg-white" data-testid="verify-result">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Verificación · {verifyResult.env}/{verifyResult.filename}
                </span>
                <Badge variant="outline" className={`text-[9px] ${verifyResult.ok ? "border-green-300 text-green-800 bg-green-50" : "border-red-300 text-red-800 bg-red-50"}`}>
                  {verifyResult.ok ? "OK" : "ERRORES"}
                </Badge>
              </div>
              {verifyResult.errors?.length > 0 && (
                <ul className="text-[11px] text-red-700 list-disc pl-5 mb-2" data-testid="verify-errors">
                  {verifyResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                <span>mongo.gz: {verifyResult.details?.has_mongo ? "✓" : "—"}</span>
                <span>uploads: {verifyResult.details?.has_uploads ? "✓" : "—"}</span>
                <span>compose: {verifyResult.details?.has_compose ? "✓" : "—"}</span>
                <span>nginx: {verifyResult.details?.has_nginx ? "✓" : "—"}</span>
              </div>
              {verifyResult.details?.manifest_json && (
                <pre className="mt-2 bg-slate-900 text-slate-100 p-2 rounded-sm text-[10px] max-h-32 overflow-auto">
                  {JSON.stringify(verifyResult.details.manifest_json, null, 2)}
                </pre>
              )}
            </div>
          )}

          {/* Restore report */}
          {restoreReport && (
            <div className="border rounded-sm p-2 bg-slate-900 text-slate-100 max-h-72 overflow-auto" data-testid="restore-report">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider mb-1.5 text-slate-300">
                <RotateCcw className="w-3 h-3" /> Resultado restauración
                <Badge variant="outline" className={`text-[9px] ${restoreReport.ok ? "border-green-500 text-green-300" : "border-red-500 text-red-300"}`}>
                  rc={restoreReport.returncode}
                </Badge>
                {restoreReport.dry_run && <Badge variant="outline" className="text-[9px] border-amber-500 text-amber-300">DRY-RUN</Badge>}
              </div>
              <pre className="text-[10px] font-mono whitespace-pre-wrap break-all leading-snug">
                {restoreReport.log}
              </pre>
            </div>
          )}

          {/* Restore CLI fallback */}
          <div className="border border-slate-200 rounded-sm p-3 bg-amber-50/40 text-[11px]" data-testid="restore-cli-reference">
            <p className="font-bold uppercase tracking-wider text-amber-900 mb-2 text-[10px] flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Restauración por CLI (siempre disponible)
            </p>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
                <code className="flex-1 font-mono text-[10px] bg-white border rounded px-2 py-1 break-all">
                  sudo bash /opt/ascauto-staging/scripts/restaurar-backup-beta &lt;tarball&gt; --dry-run
                </code>
                <Button size="sm" variant="ghost" onClick={() => copyCli("sudo bash /opt/ascauto-staging/scripts/restaurar-backup-beta <tarball> --dry-run")}
                  className="h-7 w-7 p-0" data-testid="copy-restore-beta">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-start gap-2">
                <code className="flex-1 font-mono text-[10px] bg-white border rounded px-2 py-1 break-all">
                  sudo bash /opt/ascauto/scripts/restaurar-backup-produccion &lt;tarball&gt; --dry-run
                </code>
                <Button size="sm" variant="ghost" onClick={() => copyCli("sudo bash /opt/ascauto/scripts/restaurar-backup-produccion <tarball> --dry-run")}
                  className="h-7 w-7 p-0" data-testid="copy-restore-prod">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-start gap-2">
                <code className="flex-1 font-mono text-[10px] bg-white border rounded px-2 py-1 break-all">
                  bash /opt/ascauto-staging/scripts/listar-backups
                </code>
                <Button size="sm" variant="ghost" onClick={() => copyCli("bash /opt/ascauto-staging/scripts/listar-backups")}
                  className="h-7 w-7 p-0" data-testid="copy-listar">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-amber-900 mt-2 leading-relaxed">
              Producción exige doble confirmación interactiva (<code>PRODUCCION</code> + <code>RESTAURAR ascauto.com</code>) y siempre crea un pre-backup automático antes de aplicar.
            </p>
          </div>

          {/* Comandos CLI de referencia */}
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/40 text-[11px]" data-testid="cli-reference">
            <p className="font-bold uppercase tracking-wider text-slate-700 mb-2 text-[10px]">Comandos CLI (VPS)</p>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
                <code className="flex-1 font-mono text-[10px] bg-white border rounded px-2 py-1 break-all">
                  sudo bash /opt/ascauto-staging/scripts/backup-beta
                </code>
                <Button size="sm" variant="ghost" onClick={() => copyCli("sudo bash /opt/ascauto-staging/scripts/backup-beta")}
                  className="h-7 w-7 p-0" data-testid="copy-cli-beta-ref">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex items-start gap-2">
                <code className="flex-1 font-mono text-[10px] bg-white border rounded px-2 py-1 break-all">
                  sudo bash /opt/ascauto/scripts/backup-produccion
                </code>
                <Button size="sm" variant="ghost" onClick={() => copyCli("sudo bash /opt/ascauto/scripts/backup-produccion")}
                  className="h-7 w-7 p-0" data-testid="copy-cli-prod-ref">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
              <strong>backup-produccion</strong> requiere doble confirmación interactiva en el terminal y no se puede ejecutar desde la web por seguridad.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className={`text-lg font-bold text-slate-900 ${mono ? "font-mono text-sm" : ""}`}>{value}</p>
    </div>
  );
}
